<template>
  <div class="wrapper">
    <data-filter :on-apply-filter="onApplyFilter" />

    <template v-if="isLoading">
      <div class="text-center mt-5">
        <b-spinner label="Spinning"></b-spinner>
      </div>
    </template>

    <template v-else-if="currAssetSummary && !isLoading">
      <div class="flex-container">
        <div class="flex-item iq-card" style="width: 70%;">
          <div class="iq-card-header pt-2">
            <div class="iq-card-title">
              <h5 class="card-title">Main Engine Summary</h5>
            </div>
          </div>

          <div class="iq-card-body">
            <div
              class="px-3 pb-2"
              v-if="currAssetDeviceStatus.rpmCount > 0"
            >
              <p class="mb-0">
                <b>RPM Sensors:</b> {{ currAssetDeviceStatus.rpmCount }}
                ({{ currAssetDeviceStatus.rpmTypes.join(', ') }})
              </p>
            </div>
            <div class="flex-container">
              <table
                class="table flex-item"
                v-if="currAssetDeviceStatus.rpmTypes && currAssetDeviceStatus.rpmTypes.includes('PORT')"
              >
                <thead class="thead-light">
                  <th>{{ rpmDisplayLabels.includes('PORT') ? 'Port' : 'Main' }} RPM Summary</th>
                  <th>Value</th>
                  <th>Unit</th>
                </thead>
                <tbody>
                  <tr>
                    <td><b>Average RPM</b></td>
                    <td>{{ currAssetSummary?.rpm?.PORT?.average }}</td>
                    <td>RPM</td>
                  </tr>
                  <tr>
                    <td><b>Engine Running Hour</b></td>
                    <td>{{ currAssetSummary?.rpm?.PORT?.runningTime }}</td>
                    <td>Hour</td>
                  </tr>
                </tbody>
              </table>
              <table
                class="table flex-item"
                v-if="currAssetDeviceStatus.rpmTypes && currAssetDeviceStatus.rpmTypes.includes('STARBOARD')"
              >
                <thead class="thead-light">
                  <th>{{ rpmDisplayLabels.includes('STARBOARD') ? 'Starboard' : 'Main' }} RPM Summary</th>
                  <th>Value</th>
                  <th>Unit</th>
                </thead>
                <tbody>
                  <tr>
                    <td><b>Average RPM</b></td>
                    <td>{{ currAssetSummary?.rpm?.STARBOARD?.average }}</td>
                    <td>RPM</td>
                  </tr>
                  <tr>
                    <td><b>Engine Running Hour</b></td>
                    <td>{{ currAssetSummary?.rpm?.STARBOARD?.runningTime }}</td>
                    <td>Hour</td>
                  </tr>
                </tbody>
              </table>
               <table
                class="table flex-item"
                v-if="currAssetDeviceStatus.rpmTypes && currAssetDeviceStatus.rpmTypes.includes('CENTER')"
              >
                <thead class="thead-light">
                  <th>{{ rpmDisplayLabels.includes('CENTER') ? 'Center' : 'Main' }} RPM Summary</th>
                  <th>Value</th>
                  <th>Unit</th>
                </thead>
                <tbody>
                  <tr>
                    <td><b>Average RPM</b></td>
                    <td>{{ currAssetSummary?.rpm?.CENTER?.average }}</td>
                    <td>RPM</td>
                  </tr>
                  <tr>
                    <td><b>Engine Running Hour</b></td>
                    <td>{{ currAssetSummary?.rpm?.CENTER?.runningTime }}</td>
                    <td>Hour</td>
                  </tr>
                </tbody>
              </table>

            </div>
            <div class="flex-container" v-if="currAssetDeviceStatus.hasExhaustTemperature">
              <table class="table flex-item">
                <thead class="thead-light">
                  <th>Exhaust Temperature Summary</th>
                  <th>Value</th>
                  <th>Unit</th>
                </thead>
                <tbody>
                  <tr>
                    <td><b>Avg Port Exhaust Temperature</b></td>
                    <td>{{ currAssetSummary?.temperature?.exhaust?.port?.average }}</td>
                    <td>°C</td>
                  </tr>
                  <tr>
                    <td><b>Avg Starboard Exhaust Temperature</b></td>
                    <td>{{ currAssetSummary?.temperature?.exhaust?.starboard?.average }}</td>
                    <td>°C</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div
          class="flex-item iq-card"
          style="width: 30%;"
          v-if="currAssetDeviceStatus.aeCount > 0"
        >
          <div class="iq-card-header pt-2">
            <div class="iq-card-title">
              <h5 class="card-title">Auxilliary Engine Summary</h5>
            </div>
          </div>
          <div class="iq-card-body flex-container">
            <table class="table flex-item">
              <thead class="thead-light">
                <th>Data Summary</th>
                <th>Hour</th>
                <th>Fuel Cons.</th>
              </thead>
              <tbody>
                <tr v-for="i in currAssetDeviceStatus.aeCount" :key="i">
                  <td><b>AE {{ i }}</b></td>
                  <td>{{ currAssetSummary?.ae[`ae${i}`]?.runningSeconds }}</td>
                  <td>{{ currAssetSummary?.ae[`ae${i}`]?.fuelConsumption }} Liter</td>
                </tr>
                <tr>
                  <td><b>Total</b></td>
                  <td><b>{{ currAssetSummary?.ae?.runningSeconds }}</b></td>
                  <td><b>{{ currAssetSummary?.ae?.fuelConsumption }} Liter</b></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="flex-container" v-if="currAssetDeviceStatus.flowmeterCount > 0">
        <div class="flex-item iq-card" style="width: 70%;">
          <div class="iq-card-header pt-2">
            <div class="iq-card-title">
              <h5 class="card-title">Main Engine Fuel Summary</h5>
            </div>
          </div>

          <div class="iq-card-body">
            <div>
              <div v-for="position in currAssetDeviceStatus.flowmeterPositions" :key="position" class="flex-item" style="width: 100%">
                <table v-if="currAssetDeviceStatus.flowmeterType === 'in-out'" class="table">
                  <thead class="thead-light">
                    <th>{{ capitalize(position) }} Fuel Summary</th>
                    <th>In</th>
                    <th>Out</th>
                    <th>Net</th>
                    <th>Unit</th>
                  </thead>
                  <tbody>
                    <tr>
                      <td><b>Average Flow</b></td>
                      <td>{{ currAssetSummary?.fm[position]?.in?.avgFuelFlow }}</td>
                      <td>{{ currAssetSummary?.fm[position]?.out?.avgFuelFlow }}</td>
                      <td>{{ (
                          parseFloat(currAssetSummary?.fm[position]?.in?.avgFuelFlow || '0') -
                          parseFloat(currAssetSummary?.fm[position]?.out?.avgFuelFlow || '0')
                        ).toFixed(2) }}
                      </td>
                      <td>L/h</td>
                    </tr>
                    <tr>
                      <td><b>Total Volume</b></td>
                      <td>{{ currAssetSummary?.fm[position]?.in?.engineCons }}</td>
                      <td>{{ currAssetSummary?.fm[position]?.out?.engineCons }}</td>
                      <td>{{ (
                          parseFloat(currAssetSummary?.fm[position]?.in?.engineCons || '0') -
                          parseFloat(currAssetSummary?.fm[position]?.out?.engineCons || '0')
                        ).toFixed(2) }}
                      </td>
                      <td>Liter</td>
                    </tr>
                  </tbody>
                </table>
                <table v-else class="table">
                  <thead class="thead-light">
                    <th>{{ capitalize(position) }} Fuel Summary</th>
                    <th>Value</th>
                    <th>Unit</th>
                  </thead>
                  <tbody>
                    <tr>
                      <td><b>Avg Flow</b></td>
                      <td>{{ currAssetSummary?.fm[position]?.in?.avgFuelFlow }}</td>
                      <td>L/h</td>
                    </tr>
                    <tr>
                      <td><b>Total Volume</b></td>
                      <td>{{ currAssetSummary?.fm[position]?.in?.engineCons }}</td>
                      <td>Liter</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="flex-container" v-if='currAsset?.devices?.some(d => d?.devcLabel?.toLowerCase() === "temperature")'>
              <table class="table flex-item">
                <thead class="thead-light">
                  <th>Reservoir Tank Summary</th>
                  <th>Value</th>
                  <th>Unit</th>
                </thead>
                <tbody>
                  <tr>
                    <td><b>Average Port Reservoir Temperature</b></td>
                    <td>{{ currAssetSummary?.temperature?.tank?.port?.average }}</td>
                    <td>°C</td>
                  </tr>
                  <tr>
                    <td><b>Average Starboard Reservoir Temperature</b></td>
                    <td>{{ currAssetSummary?.temperature?.tank?.port?.average }}</td>
                    <td>°C</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="flex-item iq-card" style="width: 30%;">
          <div class="iq-card-header pt-2">
            <div class="iq-card-title">
              <h5 class="card-title">Fuel Consumption Summary</h5>
            </div>
          </div>
          <div class="iq-card-body flex-container">
            <table class="table flex-item">
              <thead class="thead-light">
                <th>Fuel Summary</th>
                <th>Value</th>
                <th>Unit</th>
              </thead>
              <tbody>
                <tr>
                  <td><b>ME Fuel Cons</b></td>
                  <td>{{ currAssetSummary?.fm?.meEngineCons }}</td>
                  <td>Liter</td>
                </tr>
                <tr v-if="currAssetDeviceStatus.aeCount > 0">
                  <td><b>AE Fuel Cons</b></td>
                  <td>{{ currAssetSummary?.ae?.fuelConsumption }}</td>
                  <td>Liter</td>
                </tr>
                <tr>
                  <td><b>Total Fuel Cons</b></td>
                  <td><b>{{ (parseFloat(currAssetSummary?.fm?.meEngineCons)
                    + parseFloat(currAssetSummary?.ae?.fuelConsumption)).toFixed(2) }}</b></td>
                  <td><b>Liter</b></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="flex-container">
        <div class="flex-item map-section" style="width: 70%;">
          <Map v-if="currAsset" :ships="[currAsset]" SelectedMap="default" :center="center" :zoom="zoom"
            style="border-radius: 20px; margin-top: 10px; max-height: 100%;"
            :Options="{ enableTrail: true }" />
        </div>
        <div class="flex-item iq-card" style="width: 30%;">
          <div class="iq-card-header pt-2">
            <div class="iq-card-title">
              <h5 class="card-title">GPS Summary</h5>
            </div>
          </div>
          <div class="iq-card-body">
            <table class="table flex-item">
              <tbody>
                <tr>
                  <th><b>Average Speed</b></th>
                  <td>{{ currAssetSummary?.gps?.averageSpeed }}</td>
                  <td>KNOT</td>
                </tr>
                <tr>
                  <th><b>Total Cruise</b></th>
                  <td>{{ currAssetSummary?.gps?.totalDistance }}</td>
                  <td>NM</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="flex-container" v-if="currAssetDeviceStatus.rpmCount > 0">
        <div class="flex-item iq-card" style="width: 100%;">
          <div class="iq-card-header pt-2">
            <div class="iq-card-title">
              <h5 class="card-title">RPM, Fuel Consumption, And Speed Chart</h5>
            </div>
          </div>
          <div class="iq-card-body">
            <div class="row">
              <div class="col text-center" v-if="currAssetDeviceStatus.rpmTypes.includes('PORT')">
                <div>
                  <h5 class="">{{ currAssetSummary?.rpm?.port?.average }}</h5>
                  <span class="">Avg. {{ rpmDisplayLabels.includes('PORT') ? 'Port' : 'Main' }} RPM</span>
                </div>
              </div>
              <div class="col text-center" v-if="currAssetDeviceStatus.rpmTypes.includes('STARBOARD')">
                <div>
                  <h5 class="">{{ currAssetSummary?.rpm?.starboard?.average }}</h5>
                  <span class="">Avg. {{ rpmDisplayLabels.includes('STARBOARD') ? 'Starboard' : 'Main' }} RPM</span>
                </div>
              </div>
              <div class="col text-center">
                <div>
                  <h5 class="">{{ currAssetSummary?.gps?.averageSpeed }} Knot</h5>
                  <span class="">Avg. Speed</span>
                </div>
              </div>
            </div>
            <div class="report-chart-scroll">
              <rpm-fuel-speed :series="currAssetSummary.series" />
            </div>
          </div>
        </div>
      </div>

      <div class="flex-container" v-if="canRead('summary_operation_mode') && Object.keys(engineOpModeSummaries).length > 0">
        <div class="flex-item iq-card" style="width: 100%;">
          <div class="iq-card-header pt-2">
            <div class="iq-card-title">
              <h5 class="card-title">RPM Classification Summary</h5>
            </div>
          </div>
          <div class="iq-card-body">
            <div class="row">
              <div class="col-md-6 mb-4" v-for="(summaryData, engineName) in engineOpModeSummaries" :key="engineName">
                <div class="iq-card border shadow-none h-100">
                  <div class="iq-card-header pt-2 bg-light">
                    <div class="iq-card-title">
                      <h5 class="card-title text-capitalize">{{ engineName }}</h5>
                    </div>
                  </div>
                  <div class="iq-card-body p-0">
                    <table class="table table-striped table-bordered mb-0">
                      <thead class="thead-light">
                        <tr>
                          <th class="text-center">RPM Classification</th>
                          <th class="text-center">Duration</th>
                          <th class="text-center">Percentage</th>
                          <th class="text-center">Fuel Consumption</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, index) in summaryData" :key="index">
                          <td class="text-center">{{ item.mode }}</td>
                          <td class="text-center">{{ item.duration }}</td>
                          <td class="text-center">{{ item.percentage }}</td>
                          <td class="text-center">{{ item.consumption }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

            <div
        class="flex-container"
        v-if="currAssetDeviceStatus.hasPowermeter && currAssetSummary.powermeter.length > 0"
      >
        <div class="flex-item iq-card" style="width: 100%;">
          <div class="iq-card-header pt-2">
            <div class="iq-card-title">
              <h5 class="card-title">Powermeter Summary</h5>
            </div>
          </div>
          <div class="iq-card-body">
            <table class="table">
              <thead class="thead-light">
                <th>Name</th>
                <th>Total Energy</th>
                <th>Avg. Active Power</th>
                <th>Avg. Frequency</th>
                <th>Avg. Voltage</th>
                <th>Avg. Current</th>
                <th>Avg. Power Factor</th>
              </thead>
              <tbody>
                <tr v-for="(pm, index) in currAssetSummary.powermeter" :key="index">
                  <td><b>{{ pm.name }}</b></td>
                  <td>{{ pm.totalEnergy }} kWh</td>
                  <td>{{ pm.avgActivePower }} kW</td>
                  <td>{{ pm.avgFrequency }} Hz</td>
                  <td>{{ pm.avgVoltage }} V</td>
                  <td>{{ pm.avgCurrent }} A</td>
                  <td>{{ pm.avgPowerFactor }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div
        v-for="position in currAssetDeviceStatus.flowmeterPositions"
        :key="position"
        class="flex-container"
      >
        <div class="flex-item iq-card" style="width: 100%;">
          <div class="iq-card-header pt-2">
            <div class="iq-card-title">
              <h5 class="card-title">{{ capitalize(position) }} Fuel Characteristics</h5>
            </div>
          </div>
          <div class="iq-card-body">
            <div class="row">
              <div class="col text-center">
                <div>
                  <h5 class="">{{ currAssetSummary?.rpm[position]?.average }}</h5>
                  <span class="">Avg. {{ capitalize(position) }} RPM</span>
                </div>
              </div>
              <div class="col text-center">
                <div v-if="currAssetDeviceStatus.flowmeterType !== 'in-out'">
                  <div>
                    <h5 class="">{{ currAssetSummary?.fm[position]?.in?.avgFuelFlow }} L/h</h5>
                    <span class="">Avg. {{ capitalize(position) }} Flowrate</span>
                  </div>
                </div>
                <div v-else>
                    <div class="row">
                        <div class="col-6">
                            <h5 class="">{{ currAssetSummary?.fm[position]?.in?.avgFuelFlow }} L/h</h5>
                            <span class="">Avg. Flowrate In</span>
                        </div>
                        <div class="col-6">
                            <h5 class="">{{ currAssetSummary?.fm[position]?.out?.avgFuelFlow }} L/h</h5>
                            <span class="">Avg. Flowrate Out</span>
                        </div>
                    </div>
                </div>
              </div>
              <div class="col text-center">
                <div v-if="currAssetDeviceStatus.flowmeterType !== 'in-out'">
                  <div>
                    <h5 class="">{{ currAssetSummary?.fm[position]?.in?.avgDensity }} g/cm³</h5>
                    <span class="">Avg. {{ capitalize(position) }} Density</span>
                  </div>
                </div>
                <div v-else>
                    <div class="row">
                        <div class="col-6">
                            <h5 class="">{{ currAssetSummary?.fm[position]?.in?.avgDensity }} g/cm³</h5>
                            <span class="">Avg. Density In</span>
                        </div>
                        <div class="col-6">
                            <h5 class="">{{ currAssetSummary?.fm[position]?.out?.avgDensity }} g/cm³</h5>
                            <span class="">Avg. Density Out</span>
                        </div>
                    </div>
                </div>
              </div>
            </div>
            <div class="report-chart-scroll">
              <fuel-characteristics :series="currAssetSummary.series" :type="position" :deviceStatus="currAssetDeviceStatus" />
            </div>
          </div>
        </div>
      </div>

      <div
        class="flex-container"
        v-if="currAssetDeviceStatus.aeCount > 0"
      >
        <div class="flex-item iq-card" style="width: 100%;">
          <div class="iq-card-header pt-2">
            <div class="iq-card-title">
              <h5 class="card-title">Auxiliary Engine Usage</h5>
            </div>
          </div>
          <div class="iq-card-body">
            <div class="row">
              <div class="col text-center" v-if="currAssetDeviceStatus.aeCount > 0">
                <div>
                  <h5 class="">{{ currAssetSummary?.ae?.ae1?.runningSeconds }}</h5>
                  <span class="">AE 1 Running Hours</span>
                </div>
              </div>
              <div class="col text-center" v-if="currAssetDeviceStatus.aeCount > 1">
                <div>
                  <h5 class="">{{ currAssetSummary?.ae?.ae2?.runningSeconds }}</h5>
                  <span class="">AE 2 Running Hours</span>
                </div>
              </div>
              <div class="col text-center" v-if="currAssetDeviceStatus.aeCount > 2">
                <div>
                  <h5 class="">{{ currAssetSummary?.ae?.ae3?.runningSeconds }}</h5>
                  <span class="">AE 3 Running Hours</span>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col text-center" v-if="currAssetDeviceStatus.aeCount > 0">
                <div>
                  <h5 class="">{{ currAssetSummary?.ae?.ae1?.fuelConsumption }} Liter</h5>
                  <span class="">AE 1 Consumption</span>
                </div>
              </div>
              <div class="col text-center" v-if="currAssetDeviceStatus.aeCount > 1">
                <div>
                  <h5 class="">{{ currAssetSummary?.ae?.ae2?.fuelConsumption }} Liter</h5>
                  <span class="">AE 2 Consumption</span>
                </div>
              </div>
              <div class="col text-center" v-if="currAssetDeviceStatus.aeCount > 2">
                <div>
                  <h5 class="">{{ currAssetSummary?.ae?.ae3?.fuelConsumption }} Liter</h5>
                  <span class="">AE 3 Consumption</span>
                </div>
              </div>
            </div>
            <div class="report-chart-scroll">
              <auxiliary-engine-usage :series="currAssetSummary.series" />
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment';
import { mapState, mapActions } from 'vuex';
import Map from '../map/index.vue';
// import { LatLng } from 'leaflet';
import { buildFmsSummary, getRpmTypeLabel } from '../../util/fms-summary';
import { initAssetSummary, applyAssetSummaryFilter } from '../../util/asset-summary-loader';
import DataFilter from '../../components/DataFilter';
import AuxiliaryEngineUsage from '../../components/charts/AuxiliaryEngineUsage';
import OperationModeSvc from '../../services/OperationModeSvc';
import RpmFuelSpeed from '../../components/charts/RpmFuelSpeed';
import FuelCharacteristics from '../../components/charts/FuelCharacteristics';

export default {
  name: 'FmsReport',
  components: {
    Map,
    DataFilter,
    RpmFuelSpeed,
    FuelCharacteristics,
    AuxiliaryEngineUsage,
  },
  data() {
    return {
      // eslint-disable-next-line no-undef
      center: L.LatLng(0, 0),
      zoom: 10,
      numbers: [],
      masks: {
        input: 'DD-MM-YYYY HH:mm',
      },
      isLoading: true,
      appliedDateRange: null,
      appliedInterval: null,
      appliedMassId: null,
      opModeSummary: {},
    };
  },
  created() {
    for (let i = 600; i <= 1300; i += 100) {
      this.numbers.push(i);
    }
  },
  async mounted() {
    const date = this.$store.getters['filter/date'];
    await initAssetSummary(this.$store, {
      dateRange: date,
      allowMinute: false,
      withDevices: true,
      withLastData: false,
    });
    this.appliedDateRange = {
      start: new Date(moment(date.start).valueOf()),
      end: new Date(moment(date.end).valueOf()),
    };
    this.appliedInterval = this.$store.getters['filter/interval'];
    this.appliedMassId = this.$store.getters['filter/currentMassId'];
    await this.fetchOperationModeSummary();
    this.isLoading = false;
  },
  computed: {
    currAsset() {
      const currMassId = this.appliedMassId || this.$store.getters['filter/currentMassId'];
      const assets = this.$store.getters['asset/assets'];
      if (!assets) return null;

      return assets.find((asset) => asset.massId === currMassId);
    },
    currAssetDeviceStatus() {
      return this.currAsset?.deviceStatus || {};
    },
    rpmDisplayLabels() {
      // Map RPM types to display labels (CENTER → MAIN if single type)
      const rpmTypes = this.currAssetDeviceStatus?.rpmTypes || [];
      return rpmTypes.map((type) => getRpmTypeLabel(type, rpmTypes));
    },
    currAssetSummary() {
      const effectiveDateRange = this.appliedDateRange || this.dateRange;
      const effectiveInterval = this.appliedInterval || this.interval;
      const builtSummary = buildFmsSummary({
        deviceStatus: this.currAssetDeviceStatus,
        summary: this.currAsset?.summary,
        dateRange: effectiveDateRange,
        interval: effectiveInterval,
        aeFuelFixed: true,
      });

      return builtSummary;
    },
    dateRange: {
      get() {
        const { start, end } = this.$store.getters['filter/date'];
        const startDate = new Date(moment(start).valueOf());
        const endDate = new Date(moment(end).valueOf());

        return { start: startDate, end: endDate };
      },
      set(val) {
        const start = moment(val.start);
        const end = moment(val.end);

        this.$store.commit('filter/SET_DATE', { start, end });
      },
    },
    ...mapState('asset', ['assets']),
    ...mapState('auth', ['userAccess']),
    ...mapState('filter', {
      currentMassId: (state) => state.currentMassId,
      interval: (state) => state.interval,
    }),
    selectedInterval: {
      get() {
        return this.interval;
      },
      set(val) {
        this.$store.dispatch('filter/setInterval', val);
      },
    },
    selectedAsset: {
      get() {
        return this.currentMassId;
      },
      set(val) {
        this.$store.dispatch('filter/setCurrentMassId', val);
      },
    },
    engineOpModeSummaries() {
      return this.opModeSummary;
    },
  },
  methods: {
    capitalize(s) {
      if (typeof s !== 'string') return '';
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    },
    ...mapActions(['updatekapalTrail']),
    zoomToShip(ship) {
      // eslint-disable-next-line no-undef
      this.center = L.LatLng(ship.position[0], ship.position[1]);
      this.zoom = 15;
    },
    async onApplyFilter() {
      this.isLoading = true;
      try {
        await applyAssetSummaryFilter(this.$store, {
          dateRange: this.dateRange,
          interval: this.selectedInterval || this.interval,
          massId: this.selectedAsset || this.currentMassId,
        });
        this.appliedDateRange = {
          start: new Date(moment(this.dateRange.start).valueOf()),
          end: new Date(moment(this.dateRange.end).valueOf()),
        };
        this.appliedInterval = this.interval;
        this.appliedMassId = this.currentMassId;
        await this.fetchOperationModeSummary();
      } catch (err) {
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchOperationModeSummary() {
      this.opModeSummary = {};
      const asset = this.currAsset;

      if (!asset) return;

      const rpmDevice = asset.devices && asset.devices.find((d) => d.devcType === 'rpm');

      if (!rpmDevice) {
        return;
      }

      const { start, end } = this.dateRange;
      const startTime = moment(start).unix();
      const endTime = moment(end).unix();

      try {
        const response = await OperationModeSvc.getOperationModeSummary({
          start: startTime,
          end: endTime,
          devcId: rpmDevice.devcId,
        });

        const summaryData = response.data.data;
        const processSummary = (modes, totalRunningSeconds) => {
          if (!modes) return [];
          return Object.keys(modes).map((modeKey) => {
            const modeData = modes[modeKey];
            const percentage = totalRunningSeconds > 0
              ? ((modeData.runningSeconds / totalRunningSeconds) * 100).toFixed(2)
              : 0;
            const hours = Math.floor(modeData.runningSeconds / 3600);
            const minutes = Math.floor((modeData.runningSeconds % 3600) / 60);
            return {
              mode: modeKey,
              duration: `${hours}h ${minutes}m`,
              percentage: `${percentage}%`,
              consumption: (modeData.value || 0).toFixed(2),
            };
          });
        };

        const processedSummary = {};
        if (summaryData.engine) {
          Object.keys(summaryData.engine).forEach((engineKey) => {
            const engineModes = summaryData.engine[engineKey];
            const engineTotalSeconds = Object.values(engineModes).reduce((acc, mode) => acc + mode.runningSeconds, 0);
            processedSummary[engineKey] = processSummary(engineModes, engineTotalSeconds);
          });
        }
        this.opModeSummary = processedSummary;
      } catch (error) {
        console.error('Failed to fetch operation mode summary:', error);
        this.opModeSummary = {};
      }
    },
  },
};

</script>

<style scoped>
.flex-container {
  display: flex;
  justify-content: space-between;
  margin: 0px 10px;
}

.flex-item {
  margin: 10px 5px;
}

.table {
  width: 100%;
  table-layout: fixed;
}

.table th,
.table td {
  width: 33.33%;
  padding: 8px;
}

.map-section {
  min-height: 454px;
}

.report-chart-scroll {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

@media screen and (max-width: 991px) {
  .flex-container {
    flex-direction: column;
  }

  .flex-item {
    width: 100% !important;
    margin: 10px 0px !important;
  }

  .map-section {
    height: 300px;
    min-height: 0;
  }
}

@media screen and (max-width: 575px) {
  .iq-card-body {
    padding-left: 12px;
    padding-right: 12px;
  }

  .iq-card-body > .row {
    gap: 10px 0;
  }

  .iq-card-body > .row > .col {
    flex: 0 0 50%;
    max-width: 50%;
  }

  .iq-card-body > .row h5 {
    font-size: 1rem;
    margin-bottom: 2px;
  }

  .iq-card-body > .row span {
    font-size: 0.75rem;
    line-height: 1.2;
  }
}
</style>
